import React from 'react';
import { APP_BRAND_NAME, Locale } from '../studioLocale';
import './LandingHero.css';

interface LandingHeroProps {
  locale: Locale;
  onEnter: () => void;
}

const LandingHeroButterfly = () => (
  <svg viewBox="0 0 120 120" fill="none" aria-hidden="true">
    <path
      d="M58 35C47 18 24 15 14 31C6 45 11 65 30 73C42 78 52 74 58 61"
      fill="currentColor"
      opacity="0.12"
    />
    <path
      d="M62 35C73 18 96 15 106 31C114 45 109 65 90 73C78 78 68 74 62 61"
      fill="currentColor"
      opacity="0.12"
    />
    <path
      d="M57 63C48 57 33 58 25 70C18 82 24 95 39 99C50 102 56 95 59 84"
      fill="currentColor"
      opacity="0.1"
    />
    <path
      d="M63 63C72 57 87 58 95 70C102 82 96 95 81 99C70 102 64 95 61 84"
      fill="currentColor"
      opacity="0.1"
    />
    <path
      d="M58 35C47 18 24 15 14 31C6 45 11 65 30 73C42 78 52 74 58 61"
      stroke="currentColor"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M62 35C73 18 96 15 106 31C114 45 109 65 90 73C78 78 68 74 62 61"
      stroke="currentColor"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M57 63C48 57 33 58 25 70C18 82 24 95 39 99C50 102 56 95 59 84"
      stroke="currentColor"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M63 63C72 57 87 58 95 70C102 82 96 95 81 99C70 102 64 95 61 84"
      stroke="currentColor"
      strokeWidth="4.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M60 28C56 46 56 72 60 92" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" />
    <path d="M60 28C55 19 50 14 46 12" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
    <path d="M60 28C65 19 70 14 74 12" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
  </svg>
);

const COPY = {
  zh: {
    studio: '叙事工作室',
    motif: '蝴蝶效应创作台',
    eyebrow: '剧本生成工作台',
    title: '在记忆与舞台之间，\n让结构开始呼吸。',
    subtitle:
      '蓦浮把人物、冲突、装置与节奏放进同一块叙事画布。在这里，一次轻微改写也会像蝶翼掠过暗场，沿着人物弧线、情绪温度与结构缝隙层层传递。进入工作台之前，先让这块画布像幕布一样缓慢展开。',
    quote: '“戏剧是从书页中站起来、成为人的诗。”',
    attribution: '费德里科·加西亚·洛尔卡',
    enter: '进入工作台',
    hint: '向下进入蓦浮 Morpho',
  },
  en: {
    studio: 'Narrative Studio',
    motif: 'Butterfly Effect Studio',
    eyebrow: 'Script Generation Workbench',
    title: 'Between memory and stage,\nlet structure begin to breathe.',
    subtitle:
      'Morpho brings character, conflict, device, and rhythm onto one narrative canvas. Here, the smallest revision moves like a butterfly across blackout air, passing through arc, structure, and emotional temperature in widening rings. Before the workbench opens, let that canvas unfold like a curtain.',
    quote: '"Theatre is poetry that rises from the book and becomes human."',
    attribution: 'Federico Garcia Lorca',
    enter: 'Enter the workbench',
    hint: 'Enter Morpho below',
  },
} as const;

const LandingHero: React.FC<LandingHeroProps> = ({ locale, onEnter }) => {
  const text = COPY[locale];

  return (
    <section className="landing-hero" aria-label={text.eyebrow}>
      <div className="landing-hero__veil" />
      <div className="landing-hero__grain" />
      <div className="landing-hero__wash landing-hero__wash--one" />
      <div className="landing-hero__wash landing-hero__wash--two" />
      <div className="landing-hero__wash landing-hero__wash--three" />

      <div className="landing-hero__inner">
        <div className="landing-hero__copy">
          <div className="landing-hero__brand">
            <span className="landing-hero__brand-emblem">
              <LandingHeroButterfly />
            </span>
            <div className="landing-hero__brand-lockup">
              <span className="landing-hero__brand-name">{APP_BRAND_NAME}</span>
              <span className="landing-hero__brand-studio">{text.studio}</span>
            </div>
            <span className="landing-hero__brand-motif">{text.motif}</span>
          </div>
          <div className="landing-hero__eyebrow">{text.eyebrow}</div>
          <h1 className="landing-hero__title">
            {text.title.split('\n').map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h1>
          <p className="landing-hero__subtitle">{text.subtitle}</p>
        </div>

        <figure className="landing-hero__quote-block">
          <blockquote className="landing-hero__quote">{text.quote}</blockquote>
          <figcaption className="landing-hero__attribution">{text.attribution}</figcaption>
        </figure>
      </div>

      <button className="landing-hero__enter" type="button" onClick={onEnter} aria-label={text.enter}>
        <span className="landing-hero__enter-ring" />
        <span className="landing-hero__enter-arrow">↓</span>
        <span className="landing-hero__enter-label">{text.hint}</span>
      </button>
    </section>
  );
};

export default LandingHero;
